import { useLazyGetConvertApiTokenQuery } from "@api/media";
import { useCreateMediaFolderMutation } from "@api/media-folders";
import {
  extractMediaFromPptx,
  type IMediaLibUploadingFile,
  MediaLibCategoryEnum,
  sendMediaLibUploadErrorToast,
  sendMediaLibUploadedToast,
  sendMediaLibUploadingToast,
  useMediaLibUploadingStore,
} from "@editor/modules/media-lib";
import { nanoid } from "@reduxjs/toolkit";
import { useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { track } from "@/features/analytics";
import {
  fetchImagesDimensions,
  type IImageDimensionsData,
  useUploadMedia,
} from "@/shared";
import { useGetCurrentUserQuery } from "@/shared/services";
import type { IConvertApiExtractImagesResponse } from "@/shared/types";
import {
  type IMediaFile,
  type IMediaUploadResponse,
  MediaFolderPermEnum,
} from "@/shared/types";
import { useMediaLibImportStore } from "../stores/media-lib-import.store";
import { useMediaLibOpenFilePicker } from "./use-media-lib-open-file-picker";

interface IProps {
  onUploadStart?: () => void;
  setCategory: (value: MediaLibCategoryEnum) => void;
  setSelectedFolderId: (value: string | null) => void;
  onAddFiles: (value: IMediaFile[]) => void;

  skipAbort?: boolean;
}

type IImportedFile = {
  fileName: string;
} & ({ url: string } | { blob: Blob });

export const useMediaLibImportImages = ({
  onUploadStart,
  onAddFiles,
  setCategory,
  setSelectedFolderId,
  skipAbort,
}: IProps) => {
  const controller = useRef(new AbortController());

  const setIsImporting = useMediaLibImportStore((s) => s.setIsImporting);
  const setUploadingFiles = useMediaLibUploadingStore(
    (s) => s.setUploadingFiles,
  );

  const { uploadMediaFromUrl, uploadMedia } = useUploadMedia();
  const [createFolder] = useCreateMediaFolderMutation();
  const { data: currentUser } = useGetCurrentUserQuery();
  const { openFilePicker: handleOpenPicker } = useMediaLibOpenFilePicker({
    accept:
      "application/pdf,application/vnd.openxmlformats-officedocument.presentationml.presentation",

    multiple: false,
  });
  const [fetchConvertApiToken] = useLazyGetConvertApiTokenQuery();

  useEffect(() => {
    return () => {
      if (!skipAbort) {
        controller.current.abort();
      }
    };
  }, []);

  const uploadFiles = async (files: IImportedFile[], folderId: string) => {
    const promises: Promise<IMediaUploadResponse>[] = [];
    const uploadingFiles: IMediaLibUploadingFile[] = [];

    let sendErrorToast = false;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      uploadingFiles.push({
        id: nanoid(),
        type: MediaLibCategoryEnum.Import,
        index: i,
        folderId,
      });

      promises.push(
        new Promise(async (resolve, reject) => {
          try {
            if ("url" in file) {
              const mediaFile = await uploadMediaFromUrl(file.url, {
                title: file.fileName,
                folderId,
                signal: controller.current.signal,
              });
              resolve(mediaFile);
            } else if ("blob" in file) {
              const mediaFile = await uploadMedia({
                title: file.fileName,
                blob: file.blob,
                fileType: file.blob.type,
                signal: controller.current.signal,
                folderId,
              });
              resolve(mediaFile);
            }
          } catch (e) {
            console.log(e);
            track("failed to upload imported media file", {
              file,
              error: e,
            });
            sendErrorToast = true;
            reject();
          }
        }),
      );
    }

    setUploadingFiles(uploadingFiles);
    const settled = await Promise.allSettled(promises);

    if (!controller.current.signal.aborted) {
      let sendSuccessToast = false;
      const uploadedFiles: IMediaUploadResponse[] = [];
      for (const s of settled) {
        if (s.status === "fulfilled") {
          sendSuccessToast = true;
          uploadedFiles.push(s.value);
        }
      }
      onAddFiles(uploadedFiles);
      if (sendSuccessToast) {
        sendMediaLibUploadedToast({ msg: "Uploaded" });
      }
      if (sendErrorToast) {
        sendMediaLibUploadErrorToast({
          title: "Upload failed",
        });
      }
    }
    setUploadingFiles([]);
  };

  const extractMediaFromPdf = async (file: File): Promise<IImportedFile[]> => {
    const tokenResp = await fetchConvertApiToken({}).unwrap();
    const formData = new FormData();
    formData.append("File", file);
    formData.append("StoreFile", "true");
    formData.append("ImageOutputFormat", "png");
    const resp = await fetch(
      `https://v2.convertapi.com/convert/pdf/to/extract-images?Token=${tokenResp.token}`,
      {
        method: "POST",
        body: formData,
        signal: controller.current.signal,
      },
    );
    const result = (await resp.json()) as IConvertApiExtractImagesResponse;

    if (
      !result ||
      !Object.hasOwn(result, "Files") ||
      !Array.isArray(result.Files)
    ) {
      throw new Error("No files found");
    }
    const imgs = result.Files.filter(
      (f) => !["tif", "tiff", "tiff-fax"].includes(f.FileExt),
    ).map((f) => ({
      fileName: f.FileName,
      url: f.Url,
    }));

    const promises = imgs.map((img) =>
      fetchImagesDimensions(img.url, img.fileName),
    );
    const res = await Promise.allSettled(promises);

    return res
      .filter(
        (r): r is PromiseFulfilledResult<IImageDimensionsData> =>
          r.status === "fulfilled" && r.value.height > 30 && r.value.width > 30,
      )
      .map((r) => ({
        fileName: r.value.id,
        url: r.value.url,
      }));
  };

  const openImportFilePicker = () => {
    handleOpenPicker(async (files) => {
      if (files.length === 0 || !currentUser) return;
      setIsImporting(true);
      const toastId = sendMediaLibUploadingToast({
        onCancel: () => controller.current.abort(),
      });

      try {
        controller.current.abort();
        controller.current = new AbortController();

        const importedFiles: IImportedFile[] = [];
        const file = files[0];
        if (file.type === "application/pdf") {
          importedFiles.push(...(await extractMediaFromPdf(file)));
        } else if (
          file.type ===
          "application/vnd.openxmlformats-officedocument.presentationml.presentation"
        ) {
          const media: IImportedFile[] = (await extractMediaFromPptx(file)).map(
            (el) => ({ fileName: el.name, blob: el.blob }),
          );
          importedFiles.push(...media);
        }

        if (importedFiles.length === 0) {
          toast.info("We couldn’t find anything to import.");
          return;
        }

        if (onUploadStart) onUploadStart();

        const folderResp = await createFolder({
          title: files[0].name,
          parentId: MediaLibCategoryEnum.Import,
          users: [{ id: currentUser.oktaId, type: MediaFolderPermEnum.Owner }],
          signal: controller.current.signal,
        }).unwrap();
        setCategory(MediaLibCategoryEnum.Import);
        setSelectedFolderId(folderResp._id);

        await uploadFiles(importedFiles, folderResp._id);
      } catch (e) {
        if (!controller.current.signal.aborted) {
          track("error extracting images from import", {
            error: e,
          });
          toast.error("Error extracting media");
        }
        console.log(e);
      } finally {
        setIsImporting(false);
        toast.dismiss(toastId);
      }
    });
  };

  return { openImportFilePicker };
};
